/**
 * ONE print mode for the whole terminal (owner 2026-10-08: "Print / PDF prints an incomplete report, much is invisible
 * or missing"). Every page prints through this module, whether the reader presses a page's Print button (printPage)
 * or the browser's own Print command (the beforeprint / afterprint listeners installed once by installPrintHandlers).
 *
 * While print mode is on:
 *   - <html data-print="1"> scopes the print stylesheet (src/print/print.css): scroll containers unrolled, chrome hidden,
 *     tables and figures kept whole, backgrounds kept (print-color-adjust: exact);
 *   - the document is switched to the light token set (data-theme="light"), dark islands included, so the page prints
 *     in an ink-saving palette with full contrast whatever the screen theme; the previous themes are restored after;
 *   - every closed <details> is opened, and every React disclosure that reads usePrintMode() renders its body; the
 *     previous open / closed state is restored after printing;
 *   - charts sized by useWidth (chartKit) are drawn at the printable width (PRINT_WIDTH_PX), never wider.
 * Nothing on the page is recomputed: print mode only changes what is shown and how it is laid out.
 */
import { useSyncExternalStore } from 'react';
import { flushSync } from 'react-dom';

/** A4 portrait less 15 mm side margins = 180 mm of content, at 96 CSS px per inch. */
export const PRINT_WIDTH_MM = 180;
export const PRINT_WIDTH_PX = Math.floor((PRINT_WIDTH_MM / 25.4) * 96); // 680

/** What a page states about itself in the running header of every printed page. */
export interface PrintMeta { title: string; asset?: string; asOf?: string; runId?: string; snapshot?: string }

let printing = false;
const listeners = new Set<() => void>();
let meta: PrintMeta | null = null;

interface Saved { theme: string | null; islands: Element[]; details: HTMLDetailsElement[]; style: HTMLStyleElement | null; zoomed: [HTMLElement, string][]; frozen: [HTMLElement, string][] }

// Horizontal room a table has on paper: the printable width less the padding, border and margin of its ancestors.
function roomFor(el: HTMLElement): number {
  let used = 0;
  for (let a = el.parentElement; a && a !== document.body; a = a.parentElement) {
    const cs = window.getComputedStyle(a);
    used += ['paddingLeft', 'paddingRight', 'borderLeftWidth', 'borderRightWidth', 'marginLeft', 'marginRight']
      .reduce((n, k) => n + (parseFloat((cs as any)[k]) || 0), 0);
  }
  return Math.max(240, PRINT_WIDTH_PX - Math.min(used, 200) - 4);
}

/** A table whose narrowest layout (print fonts, wrapping cells) is still wider than the page is scaled to fit. */
function fitTables(into: [HTMLElement, string][]) {
  document.querySelectorAll<HTMLTableElement>('#root table').forEach((t) => {
    const w = t.style.width;
    t.style.setProperty('width', 'min-content', 'important');
    const need = t.getBoundingClientRect().width;
    if (w) t.style.width = w; else t.style.removeProperty('width');
    const room = roomFor(t);
    if (need > room) {
      into.push([t, t.style.getPropertyValue('zoom')]);
      t.style.setProperty('zoom', String(Math.max(0.45, Math.floor((room / need) * 100) / 100)));
    }
  });
}
/** A drawing that cannot reflow (a node map) keeps its screen size and is scaled to the printable width. */
function freezeScaled(into: [HTMLElement, string][]) {
  document.querySelectorAll<HTMLElement>('[data-print-scale]').forEach((el) => {
    const r = el.getBoundingClientRect();
    if (!r.width) return;
    into.push([el, el.getAttribute('style') || '']);
    el.style.setProperty('width', `${Math.round(r.width)}px`, 'important');
    el.style.setProperty('height', `${Math.round(r.height)}px`, 'important');
    el.style.setProperty('max-width', 'none', 'important');
    const room = roomFor(el);
    if (r.width > room) el.style.setProperty('zoom', String(Math.floor((room / r.width) * 100) / 100));
  });
}
let saved: Saved | null = null;

export function isPrinting(): boolean { return printing; }

export function subscribePrint(fn: () => void): () => void {
  listeners.add(fn);
  return () => { listeners.delete(fn); };
}

const notify = () => { listeners.forEach((fn) => fn()); };

/** True while the page is being printed; disclosures render their bodies, charts take the print width. */
export function usePrintMode(): boolean {
  return useSyncExternalStore(subscribePrint, isPrinting, () => false);
}

/** The page's running-header facts (set by <PrintHeader>); the last mounted page wins. */
export function setPrintMeta(m: PrintMeta | null): void { meta = m; }
export function getPrintMeta(): PrintMeta | null { return meta; }

/** One line: title · asset · as of · snapshot · run (only the parts the page states). */
export function printMetaLine(m: PrintMeta | null = meta): string {
  if (!m) return '';
  return [m.title, m.asset, m.asOf ? `as of ${m.asOf}` : '', m.snapshot ? `snapshot ${m.snapshot}` : '', m.runId ? `run ${m.runId}` : '']
    .filter(Boolean).join(' · ');
}

// CSS string literal for an @page margin box (quotes and backslashes escaped, no line breaks).
const cssString = (s: string) => `"${s.replace(/\\/g, '\\\\').replace(/"/g, '\\"').replace(/[\r\n]+/g, ' ')}"`;

function openClosedDetails(into: HTMLDetailsElement[]) {
  document.querySelectorAll<HTMLDetailsElement>('details:not([open])').forEach((d) => { d.open = true; into.push(d); });
}

/** Switch the document into print mode (idempotent). */
export function enterPrintMode(): void {
  if (printing || typeof document === 'undefined') return;
  const root = document.documentElement;
  const frozen: [HTMLElement, string][] = [];
  freezeScaled(frozen); // measured in the screen layout, before anything changes
  const s: Saved = { theme: root.getAttribute('data-theme'), islands: [], details: [], style: null, zoomed: [], frozen };
  // Light tokens everywhere, the dark islands (header / footer) included.
  root.setAttribute('data-theme', 'light');
  document.querySelectorAll('[data-theme="dark"], [data-theme="graphite"]').forEach((el) => {
    if (el === root) return;
    el.setAttribute('data-print-theme', el.getAttribute('data-theme') || '');
    el.setAttribute('data-theme', 'light');
    s.islands.push(el);
  });
  root.setAttribute('data-print', '1');
  // Running header + page numbers, from the page's own statement (PrintHeader).
  const line = printMetaLine();
  // (jsdom cannot parse @page margin boxes; the unit tests read the line through printMetaLine instead.)
  if (line && !/jsdom/i.test(navigator.userAgent)) {
    const st = document.createElement('style');
    st.setAttribute('data-print-runtime', '1');
    st.textContent = `@page { @top-left { content: ${cssString(line)}; } }`;
    document.head.appendChild(st);
    s.style = st;
  }
  openClosedDetails(s.details);
  saved = s;
  printing = true;
  // Render the React disclosures open synchronously, so the print snapshot taken right after beforeprint sees them.
  try { flushSync(notify); } catch { notify(); }
  // Disclosures that just mounted may hold their own <details>.
  openClosedDetails(s.details);
  fitTables(s.zoomed);
}

/** Leave print mode and restore exactly what the reader had open, and the screen theme. */
export function exitPrintMode(): void {
  if (!printing || typeof document === 'undefined') return;
  const s = saved;
  printing = false;
  saved = null;
  const root = document.documentElement;
  root.removeAttribute('data-print');
  if (s) {
    s.details.forEach((d) => { if (d.isConnected) d.open = false; });
    s.islands.forEach((el) => {
      const t = el.getAttribute('data-print-theme');
      el.removeAttribute('data-print-theme');
      if (t) el.setAttribute('data-theme', t); else el.removeAttribute('data-theme');
    });
    if (s.theme === null) root.removeAttribute('data-theme'); else root.setAttribute('data-theme', s.theme);
    s.style?.remove();
    s.zoomed.forEach(([el, z]) => { if (z) el.style.setProperty('zoom', z); else el.style.removeProperty('zoom'); });
    s.frozen.forEach(([el, st]) => { if (st) el.setAttribute('style', st); else el.removeAttribute('style'); });
  }
  notify();
}

/** A page's Print / PDF action: print mode on, let charts re-measure, print, restore on afterprint. */
export function printPage(): void {
  enterPrintMode();
  const go = () => {
    try { window.print(); } finally {
      // window.print() blocks until the dialog closes in most browsers; afterprint also restores (idempotent).
      window.setTimeout(exitPrintMode, 0);
    }
  };
  if (typeof window.requestAnimationFrame === 'function') window.requestAnimationFrame(() => window.requestAnimationFrame(go));
  else go();
}

let installed = false;
/** The browser's own Print command (Ctrl/Cmd+P) goes through the same mode. Installed once by the app root. */
export function installPrintHandlers(): () => void {
  if (installed || typeof window === 'undefined') return () => {};
  installed = true;
  const before = () => enterPrintMode();
  const after = () => exitPrintMode();
  window.addEventListener('beforeprint', before);
  window.addEventListener('afterprint', after);
  // Browsers that do not fire beforeprint still switch the print media query.
  const mq = typeof window.matchMedia === 'function' ? window.matchMedia('print') : null;
  const onMq = (e: MediaQueryListEvent) => (e.matches ? before() : after());
  if (mq && typeof mq.addEventListener === 'function') mq.addEventListener('change', onMq);
  return () => {
    installed = false;
    window.removeEventListener('beforeprint', before);
    window.removeEventListener('afterprint', after);
    if (mq && typeof mq.removeEventListener === 'function') mq.removeEventListener('change', onMq);
  };
}
